import { AGENT_TURN_COVERAGES, type AgentTurnCoverage } from '@avisoma/shared';
import { Checkbox, CheckboxField } from '../../catalyst/checkbox';
import { Label } from '../../catalyst/fieldset';
import { Input } from '../../catalyst/input';
import { Switch, SwitchField } from '../../catalyst/switch';
import CoverageBadge from './CoverageBadge';
import type { ChatQuestionsFilters } from './chatQuestionsFilters';

const labelClass = 'block text-sm font-medium text-gray-700 dark:text-zinc-100 mb-1';

export default function ChatQuestionsFilterBar({
  filters,
  onChange,
}: {
  filters: ChatQuestionsFilters;
  onChange: (patch: Partial<ChatQuestionsFilters>) => void;
}) {
  const toggleCoverage = (coverage: AgentTurnCoverage, checked: boolean) => {
    const next = checked ? [...filters.coverage, coverage] : filters.coverage.filter((c) => c !== coverage);
    // Keep the canonical order so the query key stays stable regardless of click order.
    onChange({ coverage: AGENT_TURN_COVERAGES.filter((c) => next.includes(c)) });
  };

  return (
    <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
      <div>
        <p className={labelClass}>Coverage</p>
        <div className="flex min-h-9 flex-wrap items-center gap-x-4 gap-y-2">
          {AGENT_TURN_COVERAGES.map((coverage) => (
            <CheckboxField key={coverage} className="!gap-x-2">
              <Checkbox
                checked={filters.coverage.includes(coverage)}
                onChange={(checked) => toggleCoverage(coverage, checked)}
              />
              <Label className="cursor-pointer">
                <CoverageBadge coverage={coverage} />
              </Label>
            </CheckboxField>
          ))}
        </div>
      </div>

      <div className="flex h-9 items-center">
        <SwitchField className="!gap-x-3">
          <Label className="text-sm whitespace-nowrap">Partial or error</Label>
          <Switch
            checked={filters.partialOnly}
            // A partial or errored turn never gets a coverage verdict (it is `unknown`), so a coverage
            // selection would hide every turn this switch is meant to show.
            onChange={(partialOnly) => onChange({ partialOnly, ...(partialOnly && { coverage: [] }) })}
            aria-label="Only partial or errored turns"
          />
        </SwitchField>
      </div>

      <div className="w-72">
        <label htmlFor="chat-questions-project" className={labelClass}>
          Project ID
        </label>
        <Input
          id="chat-questions-project"
          type="search"
          placeholder="Project ID"
          value={filters.projectId}
          onChange={(e) => onChange({ projectId: e.target.value })}
        />
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label htmlFor="chat-questions-from" className={labelClass}>
            From
          </label>
          <Input
            id="chat-questions-from"
            type="date"
            value={filters.from}
            max={filters.to || undefined}
            onChange={(e) => onChange({ from: e.target.value })}
          />
        </div>
        <div>
          <label htmlFor="chat-questions-to" className={labelClass}>
            To
          </label>
          <Input
            id="chat-questions-to"
            type="date"
            value={filters.to}
            min={filters.from || undefined}
            onChange={(e) => onChange({ to: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
}
